'use client'
import { useEffect, useRef } from 'react'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import useContactForm, { FALLBACK_EMAIL } from './useContactForm'
import styles from './ContactForm.module.css'

// Inline error under a field; id matches the field's aria-describedby
function FieldError({ id, message }) {
    return message ? (
        <p id={id} className={styles.fieldError}>
            {message}
        </p>
    ) : null
}

function SubmitButton({ isSubmitting }) {
    return (
        <button type="submit" className={styles.submit} disabled={isSubmitting} aria-disabled={isSubmitting ? 'true' : undefined}>
            {isSubmitting ? (
                <>
                    <span className={styles.spinner} aria-hidden="true" />
                    Sending...
                </>
            ) : (
                <>
                    Send message
                    <span className={styles.submitArrow} aria-hidden="true">→</span>
                </>
            )}
        </button>
    )
}

// Replaces the form once it is sent. Focus moves to the heading so keyboard and screen reader users land on it.
function SentConfirmation({ onReset, headingLevel: Heading }) {
    const headingRef = useRef(null)

    useEffect(() => {
        headingRef.current?.focus()
    }, [])

    return (
        <div className={`surface-card ${styles.sent}`}>
            <span className={styles.sentIcon} aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </span>
            <Heading ref={headingRef} tabIndex={-1} className={styles.sentTitle}>
                Thanks. We have your message.
            </Heading>
            <p className={styles.sentBody}>We read every message and reply by email.</p>
            <button type="button" className={styles.sentReset} onClick={onReset}>
                Send another message
            </button>
        </div>
    )
}

function Form({ idPrefix, headingLevel, cardClassName, messageLabel, messagePlaceholder }) {
    const { executeRecaptcha } = useGoogleReCaptcha()
    const { errors, status, statusMessage, isSubmitting, getFieldProps, getErrorId, handleSubmit, reset } = useContactForm({
        executeRecaptcha,
        idPrefix,
    })

    return (
        <>
            {/* Screen-reader live region; stays mounted so changes are announced */}
            <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {statusMessage}
            </div>
            {status === 'success' ? (
                <SentConfirmation onReset={reset} headingLevel={headingLevel} />
            ) : (
                <div className={`surface-card ${cardClassName}`}>
                    <form onSubmit={handleSubmit} className={`form contact-form ${styles.form}`} id="contact_form" noValidate>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor={`${idPrefix}-name`} className="form-label-premium">Name</label>
                                <input type="text" name="name" autoComplete="name" className="form-control marketing-input" placeholder="Your name" {...getFieldProps('name')} />
                                <FieldError id={getErrorId('name')} message={errors.name} />
                            </div>
                            <div>
                                <label htmlFor={`${idPrefix}-email`} className="form-label-premium">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    inputMode="email"
                                    autoCapitalize="none"
                                    spellCheck={false}
                                    className="form-control marketing-input"
                                    placeholder="you@company.com"
                                    {...getFieldProps('email')}
                                />
                                <FieldError id={getErrorId('email')} message={errors.email} />
                            </div>
                            <div className="md:col-span-2">
                                <label htmlFor={`${idPrefix}-message`} className="form-label-premium">{messageLabel}</label>
                                <textarea name="message" className={`form-control marketing-input ${styles.message}`} placeholder={messagePlaceholder} {...getFieldProps('message')} />
                                <FieldError id={getErrorId('message')} message={errors.message} />
                            </div>
                        </div>

                        <div className={styles.footer}>
                            <SubmitButton isSubmitting={isSubmitting} />
                            <div className={styles.notes}>
                                <p className={styles.note}>We use your details to reply to you. We never sell them.</p>
                                <p className={styles.note}>
                                    All fields are required. By sending the form you agree to the <a href="/terms-and-conditions">Terms &amp; Conditions</a> and{' '}
                                    <a href="/privacy-policy">Privacy Policy</a>.
                                </p>
                                {/* Required notice: the reCAPTCHA badge is hidden in globals.css */}
                                <p className={styles.note}>
                                    This site is protected by reCAPTCHA and the Google{' '}
                                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" aria-label="Google Privacy Policy (opens in new tab)">
                                        Privacy Policy
                                    </a>{' '}
                                    and{' '}
                                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" aria-label="Google Terms of Service (opens in new tab)">
                                        Terms of Service
                                    </a>{' '}
                                    apply.
                                </p>
                            </div>
                        </div>

                        {status === 'error' ? (
                            <div role="alert" className={`${styles.alert} ${styles.alertError}`}>
                                Couldn&apos;t send. Email us at <a href={`mailto:${FALLBACK_EMAIL}`}>{FALLBACK_EMAIL}</a> instead.
                            </div>
                        ) : null}
                    </form>
                </div>
            )}
        </>
    )
}

/*
 * Contact form used on the homepage and /contact. Posts to /api/contact.
 * headingLevel: level of the sent confirmation heading ('h3' under a section H2).
 */
export default function ContactForm({
    idPrefix = 'contact',
    headingLevel = 'h2',
    cardClassName = 'surface-card--cta',
    messageLabel = 'Message',
    messagePlaceholder = 'What would you like to build?',
}) {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
            scriptProps={{ async: true, defer: true, appendTo: 'head' }}
        >
            <Form
                idPrefix={idPrefix}
                headingLevel={headingLevel}
                cardClassName={cardClassName}
                messageLabel={messageLabel}
                messagePlaceholder={messagePlaceholder}
            />
        </GoogleReCaptchaProvider>
    )
}
